import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Send, ArrowRight, Home as HomeIcon } from "lucide-react";
import useSEO from "../hooks/useSEO";

const COPY = {
  demande: {
    title: "Demande envoyée avec succès",
    h1: "Merci pour votre demande !",
    intro:
      "Votre besoin a bien été transmis à notre équipe. Un email récapitulatif vient de partir vers notre service client.",
    next: [
      "Notre équipe étudie votre demande et identifie le meilleur prestataire francophone.",
      "Vous recevez une réponse personnalisée par email ou téléphone, selon votre préférence.",
      "Vous bénéficiez d'un accompagnement humain, fiable et sans frais cachés.",
    ],
    cta_primary: { label: "Explorer les prestataires", to: "/recherche" },
    cta_secondary: { label: "Retour à l'accueil", to: "/" },
  },
  contact: {
    title: "Message reçu",
    h1: "Merci de nous avoir écrit !",
    intro:
      "Votre message est bien arrivé dans nos boîtes. Nous y répondons généralement sous 24 à 48 heures ouvrables.",
    next: [
      "Un membre de notre équipe va examiner votre message attentivement.",
      "Vous recevrez une réponse à l'adresse email que vous avez fournie.",
      "Pour toute urgence, vous pouvez aussi nous joindre au +1 (418) 473-5188.",
    ],
    cta_primary: { label: "Découvrir DemkaBiz", to: "/a-propos" },
    cta_secondary: { label: "Retour à l'accueil", to: "/" },
  },
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const type = params.get("type") === "contact" ? "contact" : "demande";
  const name = params.get("name") || "";
  const c = COPY[type];

  useSEO({ title: c.title, description: c.intro });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="thank-you-page">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" data-testid="thank-you-icon" />
        </div>
        <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">{c.title}</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
          {name ? `Merci ${name} !` : c.h1}
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed" data-testid="thank-you-intro">
          {c.intro}
        </p>
      </div>

      <div className="card-surface p-6 sm:p-8 mt-10">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          Et ensuite ?
        </h2>
        <ul className="space-y-3 mt-4">
          {c.next.map((step, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`thank-you-step-${i}`}>
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary text-primary flex items-center justify-center text-xs font-bold mt-0.5">
                {i + 1}
              </span>
              <p className="text-sm text-foreground/80 leading-relaxed">{step}</p>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6 mt-6 text-center">
        <p className="text-sm text-foreground/80">
          <strong className="text-accent">Bonus :</strong> rejoignez notre communauté Telegram pour échanger avec d'autres nouveaux arrivants et professionnels francophones.
        </p>
        <a
          href="https://t.me/+9LcRsRIqlgRiNDIx"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="thank-you-telegram-cta"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-accent text-white hover:bg-accent/90 transition text-sm font-medium"
        >
          <Send className="w-4 h-4" />
          Rejoindre la communauté Telegram
        </a>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-10 justify-center">
        <Link to={c.cta_primary.to} className="btn-primary justify-center" data-testid="thank-you-cta-primary">
          {c.cta_primary.label} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to={c.cta_secondary.to} className="btn-outline justify-center" data-testid="thank-you-cta-secondary">
          <HomeIcon className="w-4 h-4" /> {c.cta_secondary.label}
        </Link>
      </div>
    </div>
  );
}
